import dayjs from "dayjs";
import { NegotiationsReportItem } from "../../../api";
import { CASE_LEVEL_COLUMNS } from "./constants";
import type { RowWithSpans, SortableColumn, SortDirection, SortRule } from "./types";
import { DEFAULT_SORT_RULES } from "./types";

function getSortValue(row: NegotiationsReportItem, column: SortableColumn): string | number {
  switch (column) {
    case "caseName":
      return row.caseName?.toLowerCase() ?? "";
    case "raa":
      return row.raa?.toLowerCase() ?? "";
    case "taskName":
      return row.taskName?.toLowerCase() ?? "";
    case "taskAssignee":
      return row.taskAssignee?.toLowerCase() ?? "";
    case "status":
      return row.status?.toLowerCase() ?? "";
    case "negotiationStartedAt":
      return row.negotiationStartedAt
        ? dayjs(row.negotiationStartedAt).valueOf()
        : Number.MAX_SAFE_INTEGER;
    case "taskedAt":
      return row.taskedAt ? dayjs(row.taskedAt).valueOf() : Number.MAX_SAFE_INTEGER;
    case "dueDate":
      return row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
    default:
      return "";
  }
}

function compareSortValues(
  aValue: string | number,
  bValue: string | number,
  order: SortDirection
) {
  if (aValue < bValue) return order === "asc" ? -1 : 1;
  if (aValue > bValue) return order === "asc" ? 1 : -1;
  return 0;
}

function getDefaultTaskOrder(a: NegotiationsReportItem, b: NegotiationsReportItem) {
  const aTasked = a.taskedAt ? dayjs(a.taskedAt).valueOf() : Number.MAX_SAFE_INTEGER;
  const bTasked = b.taskedAt ? dayjs(b.taskedAt).valueOf() : Number.MAX_SAFE_INTEGER;
  if (aTasked !== bTasked) return aTasked - bTasked;

  const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  return aDue - bDue;
}

function compareByRules(a: NegotiationsReportItem, b: NegotiationsReportItem, rules: SortRule[]) {
  for (const rule of rules) {
    const result = compareSortValues(
      getSortValue(a, rule.column),
      getSortValue(b, rule.column),
      rule.direction
    );
    if (result !== 0) return result;
  }
  return getDefaultTaskOrder(a, b);
}

function getGroupSortValue(group: NegotiationsReportItem[], column: SortableColumn): string | number {
  const first = group[0];
  if (!first) return "";

  if (CASE_LEVEL_COLUMNS.includes(column)) {
    return getSortValue(first, column);
  }

  if (column === "dueDate" || column === "taskedAt") {
    return Math.min(
      ...group.map((row) => {
        const value = column === "dueDate" ? row.dueDate : row.taskedAt;
        return value ? dayjs(value).valueOf() : Number.MAX_SAFE_INTEGER;
      })
    );
  }

  return group.reduce<string | number>((best, row) => {
    const value = getSortValue(row, column);
    if (best === "") return value;
    return compareSortValues(value, best, "asc") < 0 ? value : best;
  }, "");
}

function compareGroupsByRules(
  groupA: NegotiationsReportItem[],
  groupB: NegotiationsReportItem[],
  rules: SortRule[]
) {
  for (const rule of rules) {
    const result = compareSortValues(
      getGroupSortValue(groupA, rule.column),
      getGroupSortValue(groupB, rule.column),
      rule.direction
    );
    if (result !== 0) return result;
  }
  return (groupA[0]?.caseName ?? "").localeCompare(groupB[0]?.caseName ?? "");
}

export function groupRowsByCase(rows: NegotiationsReportItem[]): NegotiationsReportItem[][] {
  const groups: NegotiationsReportItem[][] = [];
  let currentGroup: NegotiationsReportItem[] = [];
  let currentCaseName = "";

  rows.forEach((row) => {
    if (row.caseName !== currentCaseName) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [row];
      currentCaseName = row.caseName ?? "";
    } else {
      currentGroup.push(row);
    }
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

function normalizeSortRules(rules: SortRule[] | undefined): SortRule[] {
  if (!rules?.length) return [...DEFAULT_SORT_RULES];
  const seen = new Set<SortableColumn>();
  const normalized: SortRule[] = [];
  rules.forEach((rule) => {
    if (seen.has(rule.column)) return;
    seen.add(rule.column);
    normalized.push(rule);
  });
  return normalized.length > 0 ? normalized : [...DEFAULT_SORT_RULES];
}

export function sortReportRows(
  rows: NegotiationsReportItem[],
  rules: SortRule[]
): NegotiationsReportItem[] {
  const sortRules = normalizeSortRules(rules);
  const groups = groupRowsByCase(rows);

  groups.sort((groupA, groupB) => compareGroupsByRules(groupA, groupB, sortRules));

  const primaryIsCaseLevel = CASE_LEVEL_COLUMNS.includes(sortRules[0]?.column ?? "caseName");

  return groups.flatMap((group) =>
    [...group].sort((a, b) =>
      primaryIsCaseLevel && sortRules.every((rule) => CASE_LEVEL_COLUMNS.includes(rule.column))
        ? getDefaultTaskOrder(a, b)
        : compareByRules(a, b, sortRules)
    )
  );
}

export function computeCaseGroupSpans(rows: NegotiationsReportItem[]): RowWithSpans[] {
  const out: RowWithSpans[] = [];
  let i = 0;
  let caseNumber = 0;

  while (i < rows.length) {
    const caseName = rows[i]?.caseName ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.caseName ?? "") === caseName) j++;

    const span = j - i;
    caseNumber += 1;

    for (let k = i; k < j; k++) {
      out.push({
        ...rows[k],
        rowNumber: k === i ? caseNumber : undefined,
        groupRowSpan: k === i ? span : undefined,
        skipCaseColumns: k !== i,
        isLastInCaseGroup: k === j - 1,
      });
    }

    i = j;
  }

  return out;
}
